import type { ReactNode } from "react";
import { useEmbeddedAllCaps } from "../hooks/useEmbeddedAllCaps";
import { TL4KAppFooter } from "./TL4KAppFooter";

/**
 * Shell standard per le app TL4K senza `TL4KAppHeader` (o senza toggle proprio):
 * applica il maiuscolo globale (useEmbeddedAllCaps: ?allCaps=1 + postMessage
 * TL4K_ALL_CAPS dal parent TechyWeb) e monta il footer normativo quando
 * standalone (in iframe il footer della shell esterna copre le pagine legali).
 *
 * Uso:
 * ```tsx
 * <ThemeProvider ageGroup="7-10">
 *   <TL4KAppShell appLabel="Anonimizza">
 *     <App />
 *   </TL4KAppShell>
 * </ThemeProvider>
 * ```
 */
export function TL4KAppShell({
  appLabel,
  fitViewport = false,
  children,
}: {
  appLabel: string;
  /**
   * Altezza VINCOLATA alla finestra invece che semplicemente "almeno" la
   * finestra (`height` invece di `min-height`).
   *
   * Serve alle app che gestiscono da sé lo scorrimento interno e non devono mai
   * far scorrere la pagina (il configuratore IoT a 1376x768). Con `min-height`
   * l'altezza del guscio la decide il contenuto, quindi un figlio con `flex-1`
   * non ha uno spazio residuo su cui calcolarsi e non si riduce mai: è la
   * ragione per cui i pannelli interni sfondavano invece di scorrere.
   *
   * Resta `false` di default: le app che crescono in verticale e si affidano
   * allo scroll della pagina continuano a comportarsi esattamente come prima.
   */
  fitViewport?: boolean;
  children: ReactNode;
}) {
  const [allCaps] = useEmbeddedAllCaps(false);
  const isEmbedded =
    typeof window !== "undefined" && window.parent !== window;

  return (
    <div
      style={{
        ...(fitViewport
          ? { height: "100dvh", overflow: "hidden" }
          : { minHeight: "100dvh" }),
        display: "flex",
        flexDirection: "column",
        textTransform: allCaps ? "uppercase" : undefined,
      }}
    >
      {children}
      {!isEmbedded && <TL4KAppFooter appLabel={appLabel} />}
    </div>
  );
}
